import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuthProfile } from '@/context/AuthContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { RoleGuard } from '@/components/auth/RoleGuard';
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";

// Lazy loading das páginas
const AuthPage = lazy(() => import("./pages/AuthPage"));
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PlayersPage = lazy(() => import("./pages/PlayersPage"));
const GroupsPage = lazy(() => import("./pages/GroupsPage"));
const NovoGrupoPage = lazy(() => import("./pages/NovoGrupoPage"));
const NovoJogadorPage = lazy(() => import("./pages/NovoJogadorPage"));
const RoundsPage = lazy(() => import("./pages/RoundsPage"));
const MatchesPage = lazy(() => import("./pages/MatchesPage"));
const MinhasPartidas = lazy(() => import("./pages/MinhasPartidas"));
const MinhasDisponibilidades = lazy(() => import("./pages/MinhasDisponibilidades.tsx"));
const RankingsPage = lazy(() => import("./pages/RankingsPage"));
const GroupDetailPage = lazy(() => import("./pages/GroupDetailPage"));
const PlayerDetailPage = lazy(() => import("./pages/PlayerDetailPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NovaRodadaPage = lazy(() => import("./pages/NovaRodadaPage"));
const DisponibilidadePage = lazy(() => import("./pages/DisponibilidadePage"));
const RodadaDetailPage = lazy(() => import("./pages/RodadaDetailPage"));
const DisponibilidadeEditarPage = lazy(() => import("./pages/DisponibilidadeEditarPage"));
const PerfilPage = lazy(() => import("./pages/PerfilPage"));

const queryClient = new QueryClient();

// Componente de loading
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Componente que detecta hash fragments do Supabase e redireciona
const HashRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;
    const currentSearch = window.location.search;
    
    // Caso 1: Hash com access_token (formato esperado)
    if (currentHash && currentHash.includes('access_token') && currentPath !== '/auth') {
      console.log('🔄 Detectado callback do Supabase no hash');
      console.log('🔄 Hash preservado:', currentHash.substring(0, 50) + '...');
      // Usa window.location.href para preservar o hash
      window.location.href = '/auth' + currentHash;
      return;
    }
    
    // Caso 2: access_token no pathname (quando Supabase envia /#access_token mas BrowserRouter interpreta como rota)
    if (currentPath.includes('access_token')) {
      console.log('🔄 Detectado callback do Supabase no pathname:', currentPath);
      // Extrai os parâmetros do pathname e converte para hash
      const params = currentPath.substring(1); // Remove a barra inicial
      console.log('🔄 Redirecionando para /auth com hash:', params.substring(0, 50) + '...');
      // Redireciona para /auth com os parâmetros como hash
      window.location.href = '/auth#' + params;
    }
  }, [navigate, location]);

  return null;
};

// Wrapper para Layout que verifica sessão antes de renderizar
const ProtectedLayout = () => {
  const { loading, session } = useAuthProfile();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!session) {
    return <Navigate to="/auth" replace />;
  }
  
  return <Layout />;
};

const App = () => {
  // Remove o loader inicial quando o App monta
  useEffect(() => {
    const loader = document.getElementById('initial-loader');
    if (loader) {
      console.log('✅ App montado - removendo loader inicial');
      loader.remove();
    }
  }, []);

  // Sempre usa BrowserRouter para evitar conflito com hash fragments do Supabase
  const Router: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <BrowserRouter>{children}</BrowserRouter>
  );
  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Router>
          <AuthProvider>
            <HashRedirect />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/auth" element={<AuthPage />} />
              <Route path="/" element={<ProtectedLayout />}>
                <Route index element={<ProtectedRoute><RoleGuard roles={['admin', 'jogador']}><Index /></RoleGuard></ProtectedRoute>} />
                <Route path="jogadores" element={<ProtectedRoute><RoleGuard roles={['admin', 'jogador']}><PlayersPage /></RoleGuard></ProtectedRoute>} />
                <Route path="jogadores/:id" element={<ProtectedRoute><RoleGuard roles={['admin']}><PlayerDetailPage /></RoleGuard></ProtectedRoute>} />
                <Route path="grupos" element={<ProtectedRoute><RoleGuard roles={['admin', 'jogador']}><GroupsPage /></RoleGuard></ProtectedRoute>} />
                <Route path="grupos/:id" element={<ProtectedRoute><RoleGuard roles={['admin']}><GroupDetailPage /></RoleGuard></ProtectedRoute>} />
                <Route path="admin/novo-grupo" element={<ProtectedRoute><RoleGuard roles={['admin']}><NovoGrupoPage /></RoleGuard></ProtectedRoute>} />
                <Route path="admin/novo-jogador" element={<ProtectedRoute><RoleGuard roles={['admin']}><NovoJogadorPage /></RoleGuard></ProtectedRoute>} />
                <Route path="admin/nova-rodada" element={<ProtectedRoute><RoleGuard roles={['admin']}><NovaRodadaPage /></RoleGuard></ProtectedRoute>} />
                <Route path="rodadas" element={<ProtectedRoute><RoleGuard roles={['admin']}><RoundsPage /></RoleGuard></ProtectedRoute>} />
                <Route path="rodadas/:id" element={<ProtectedRoute><RoleGuard roles={['admin']}><RodadaDetailPage /></RoleGuard></ProtectedRoute>} />
                <Route path="partidas" element={<ProtectedRoute><RoleGuard roles={['jogador', 'admin']}><MatchesPage /></RoleGuard></ProtectedRoute>} />
                <Route path="minhas-partidas" element={<ProtectedRoute><RoleGuard roles={['jogador']}><MinhasPartidas /></RoleGuard></ProtectedRoute>} />
                <Route path="classificacao" element={<ProtectedRoute><RoleGuard roles={['jogador', 'admin']}><RankingsPage /></RoleGuard></ProtectedRoute>} />
                <Route path="disponibilidade" element={<ProtectedRoute><RoleGuard roles={['jogador', 'admin']}><DisponibilidadePage /></RoleGuard></ProtectedRoute>} />
                <Route path="minhas-disponibilidades" element={<ProtectedRoute><RoleGuard roles={['jogador']}><MinhasDisponibilidades /></RoleGuard></ProtectedRoute>} />
                <Route path="disponibilidade/:id/editar" element={<ProtectedRoute><RoleGuard roles={['jogador', 'admin']}><DisponibilidadeEditarPage /></RoleGuard></ProtectedRoute>} />
                <Route path="perfil" element={<ProtectedRoute><RoleGuard roles={['jogador', 'admin']}><PerfilPage /></RoleGuard></ProtectedRoute>} />
                <Route path="admin" element={<ProtectedRoute><RoleGuard roles={['admin']}><AdminPage /></RoleGuard></ProtectedRoute>} />
              </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </AuthProvider>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
